import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import CommandPalette from './CommandPalette'
import PageTransition from './PageTransition'
import { useIsMobile } from '../../hooks/useMediaQuery'
import { useAuth } from '../../context/AuthContext'

const Layout = ({ userRole = 'client', children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const { impersonation, stopImpersonating } = useAuth()

  const exitImpersonation = async () => {
    await stopImpersonating()
    navigate('/admin')
  }

  // Full-screen routes — hide sidebar, header, and scroll padding.
  // The client editor is full-screen only on desktop (the drag canvas). On
  // mobile it renders the scrollable form editor, so it keeps the normal page
  // chrome (topbar + page-scroll + hamburger nav) and scrolls naturally.
  const isEditorRoute =
    location.pathname.includes('/admin/editor') ||
    (location.pathname.includes('/client/editor') && !isMobile) ||
    location.pathname.includes('/preview')

  // Close the mobile drawer whenever the route changes (a nav item was tapped).
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Close the drawer on Escape, and lock background scroll while it's open.
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false) }
    document.addEventListener('keydown', onKey)
    document.body.classList.add('sidebar-drawer-open')
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.classList.remove('sidebar-drawer-open')
    }
  }, [mobileOpen])

  // If the viewport grows back to desktop, make sure the drawer state is reset.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false)
  }, [isMobile])

  return (
    <div className="app-shell" style={impersonation ? { paddingTop: 38 } : undefined}>

      {/* Admin "view as client" banner — visible across the whole client dashboard */}
      {impersonation && (
        <div
          role="status"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            padding: '8px 16px', fontSize: 13, fontWeight: 600,
            background: 'var(--accent, #6c63ff)', color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,.15)',
          }}
        >
          <span>
            Viewing as <strong>{impersonation.client?.name || impersonation.client?.email}</strong>
            {impersonation.admin?.name ? ` (as admin ${impersonation.admin.name})` : ''}
          </span>
          <button
            type="button"
            onClick={exitImpersonation}
            style={{
              background: 'rgba(255,255,255,.2)', color: '#fff', border: '1px solid rgba(255,255,255,.5)',
              borderRadius: 99, padding: '3px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Exit
          </button>
        </div>
      )}

      {/* Sidebar — hidden on editor routes. On mobile it renders as a drawer
          driven by `mobileOpen`; on desktop it's the static rail. */}
      {!isEditorRoute && (
        <Sidebar
          collapsed={collapsed}
          onCollapse={() => setCollapsed(c => !c)}
          mobileOpen={mobileOpen}
        />
      )}

      {/* Mobile drawer backdrop — only rendered while open on small screens. */}
      {!isEditorRoute && mobileOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main area — span full width on editor routes (no sidebar present) */}
      <div className="main-area" style={isEditorRoute ? { gridColumn: '1 / -1' } : undefined}>

        {/* Topbar — hidden on editor routes */}
        {!isEditorRoute && <Header onMenuClick={() => setMobileOpen(true)} />}

        {/* Page content */}
        {isEditorRoute ? (
          <div style={{ height: '100%' }}>
            {children || <Outlet />}
          </div>
        ) : (
          <div className="page-scroll">
            <PageTransition>
              {children || <Outlet />}
            </PageTransition>
          </div>
        )}

      </div>

      {/* Command Palette — always mounted, renders nothing when closed */}
      <CommandPalette />

    </div>
  )
}

export default Layout
